import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send, Loader2, Check, CheckCheck, Bot, Image as ImageIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface Mensaje {
  id: string;
  autor_id: string;
  mensaje: string;
  created_at: string;
  leido_at: string | null;
  es_sistema: boolean;
  autor_nombre?: string;
  autor_apellido?: string;
  autor_avatar?: string | null;
}

interface ChatAtencionProps {
  citaId?: string;
  requestId?: string;
}

export function ChatAtencion({ citaId, requestId }: ChatAtencionProps) {
  const { user } = useAuth();
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingChannelRef = useRef<any>(null);

  const channelId = citaId ? `chat-cita-${citaId}` : `chat-req-${requestId}`;
  const filterCol = citaId ? 'cita_id' : 'request_id';
  const filterVal = citaId || requestId || '';

  useEffect(() => {
    fetchMensajes();

    const msgChannel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'mensajes_atencion',
        filter: `${filterCol}=eq.${filterVal}`,
      }, () => fetchMensajes())
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'mensajes_atencion',
        filter: `${filterCol}=eq.${filterVal}`,
      }, () => fetchMensajes())
      .subscribe();

    const typingChannel = supabase
      .channel(`typing-${filterVal}`)
      .on('broadcast', { event: 'typing' }, (payload) => {
        if (payload.payload?.user_id !== user?.id) {
          setOtherTyping(true);
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setOtherTyping(false), 3000);
        }
      })
      .subscribe();

    typingChannelRef.current = typingChannel;

    return () => {
      supabase.removeChannel(msgChannel);
      supabase.removeChannel(typingChannel);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [citaId, requestId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [mensajes, otherTyping]);

  useEffect(() => {
    if (!user?.id || mensajes.length === 0) return;
    const unread = mensajes.filter(m => m.autor_id !== user.id && !m.leido_at && !m.es_sistema);
    if (unread.length === 0) return;
    const ids = unread.map(m => m.id);
    supabase.from('mensajes_atencion').update({ leido_at: new Date().toISOString() }).in('id', ids)
      .then(() => {
        setMensajes(prev => prev.map(m => ids.includes(m.id) ? { ...m, leido_at: new Date().toISOString() } : m));
      });
  }, [mensajes, user?.id]);

  const fetchMensajes = async () => {
    try {
      let query = supabase.from('mensajes_atencion')
        .select('id, autor_id, mensaje, created_at, leido_at, es_sistema')
        .order('created_at', { ascending: true });
      if (citaId) query = query.eq('cita_id', citaId);
      if (requestId) query = query.eq('request_id', requestId);

      const { data, error } = await query;
      if (error) throw error;
      if (!data || data.length === 0) { setMensajes([]); setLoading(false); return; }

      const autorIds = [...new Set(data.filter(m => !m.es_sistema).map(m => m.autor_id))];
      let profileMap = new Map();
      if (autorIds.length > 0) {
        const { data: profiles } = await supabase.from('profiles')
          .select('user_id, nombre, apellido, avatar_url').in('user_id', autorIds);
        profileMap = new Map((profiles || []).map(p => [p.user_id, p]));
      }

      setMensajes(data.map(m => {
        const profile = profileMap.get(m.autor_id) as any;
        return {
          ...m,
          autor_nombre: m.es_sistema ? 'Sistema' : (profile?.nombre || 'Usuario'),
          autor_apellido: m.es_sistema ? '' : (profile?.apellido || ''),
          autor_avatar: profile?.avatar_url || null,
        };
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const broadcastTyping = useCallback(() => {
    typingChannelRef.current?.send({ type: 'broadcast', event: 'typing', payload: { user_id: user?.id } });
  }, [user?.id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNuevoMensaje(e.target.value);
    broadcastTyping();
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo se permiten imágenes'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Máximo 5MB por imagen'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile || !user?.id) return null;
    setUploading(true);
    try {
      const ext = imageFile.name.split('.').pop();
      const path = `chat/${filterVal}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('chat-images').upload(path, imageFile);
      if (error) throw error;
      const { data: urlData } = supabase.storage.from('chat-images').getPublicUrl(path);
      return urlData.publicUrl;
    } catch (err: any) {
      console.error('Upload error:', err);
      toast.error('Error al subir imagen');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSend = async () => {
    if ((!nuevoMensaje.trim() && !imageFile) || !user?.id) return;
    setSending(true);
    try {
      let msgText = nuevoMensaje.trim();

      if (imageFile) {
        const url = await uploadImage();
        if (url) {
          msgText = msgText ? `${msgText}\n📷 ${url}` : `📷 ${url}`;
        }
        clearImage();
      }

      if (!msgText) { setSending(false); return; }

      const insertData: any = { autor_id: user.id, mensaje: msgText, es_sistema: false };
      if (citaId) insertData.cita_id = citaId;
      if (requestId) insertData.request_id = requestId;

      const { error } = await supabase.from('mensajes_atencion').insert(insertData);
      if (error) throw error;
      setNuevoMensaje('');
    } catch (err: any) {
      console.error('Error sending:', err.message);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const isImageMessage = (msg: string) => msg.includes('📷 http');
  const extractImageUrl = (msg: string) => {
    const match = msg.match(/📷 (https?:\/\/\S+)/);
    return match?.[1] || null;
  };
  const getTextWithoutImage = (msg: string) => msg.replace(/📷 https?:\/\/\S+/g, '').trim();

  if (loading) {
    return <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="flex flex-col h-[350px]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 p-3 bg-muted/30 rounded-lg">
        {mensajes.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">No hay mensajes aún. ¡Inicia la conversación!</p>
        ) : (
          mensajes.map((msg) => {
            const isOwn = msg.autor_id === user?.id;
            if (msg.es_sistema) {
              return (
                <div key={msg.id} className="flex justify-center my-2">
                  <div className="flex items-center gap-1.5 bg-muted/80 rounded-full px-3 py-1.5 max-w-[85%]">
                    <Bot className="w-3 h-3 text-muted-foreground shrink-0" />
                    <p className="text-xs text-muted-foreground text-center">{msg.mensaje}</p>
                  </div>
                </div>
              );
            }

            const imgUrl = extractImageUrl(msg.mensaje);
            const textOnly = getTextWithoutImage(msg.mensaje);

            return (
              <div key={msg.id} className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
                <Avatar className="h-7 w-7 shrink-0">
                  {msg.autor_avatar && <AvatarImage src={msg.autor_avatar} />}
                  <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                    {msg.autor_nombre?.[0]}{msg.autor_apellido?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="max-w-[75%]">
                  <div className={cn(
                    'rounded-2xl px-3 py-2 text-sm',
                    isOwn ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-background border rounded-bl-sm'
                  )}>
                    {textOnly && <p>{textOnly}</p>}
                    {imgUrl && (
                      <a href={imgUrl} target="_blank" rel="noopener noreferrer" className="block mt-1">
                        <img src={imgUrl} alt="Imagen adjunta" className="rounded-lg max-w-full max-h-48 object-cover" />
                      </a>
                    )}
                  </div>
                  <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end' : ''}`}>
                    <p className="text-[10px] text-muted-foreground">
                      {msg.autor_nombre} · {new Date(msg.created_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {isOwn && (msg.leido_at ? <CheckCheck className="w-3 h-3 text-primary" /> : <Check className="w-3 h-3 text-muted-foreground" />)}
                  </div>
                </div>
              </div>
            );
          })
        )}

        {otherTyping && (
          <div className="flex gap-2 items-center">
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarFallback className="text-[10px] bg-muted text-muted-foreground">...</AvatarFallback>
            </Avatar>
            <div className="bg-background border rounded-2xl rounded-bl-sm px-3 py-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Image preview */}
      {imagePreview && (
        <div className="relative mx-2 mt-2 inline-block">
          <img src={imagePreview} alt="Preview" className="h-20 rounded-lg border object-cover" />
          <button
            onClick={clearImage}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageSelect}
        />
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full shrink-0"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending || uploading}
        >
          <ImageIcon className="w-4 h-4 text-muted-foreground" />
        </Button>
        <Input
          placeholder="Escribe un mensaje..."
          value={nuevoMensaje}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={sending}
          className="rounded-full"
        />
        <Button
          size="icon"
          className="rounded-full shrink-0"
          onClick={handleSend}
          disabled={(sending || uploading) || (!nuevoMensaje.trim() && !imageFile)}
        >
          {sending || uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
